import React, { useState, useCallback, useRef } from 'react';
import DescribePanel from './DescribePanel';
import { getToken, setToken as saveToken, clearToken } from './hiddenAuth';

// The local CLIP+SAE describe service (Cloudflare Tunnel -> Mac). On localhost we
// talk to it directly so the page is testable in dev.
const DESCRIBE_BASE =
  process.env.REACT_APP_DESCRIBE_BASE ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8700'
    : 'https://describe.fabianmorelli.de');

// Map the service response to the shape DescribePanel expects.
function toPanel(res) {
  const d = res.description || {};
  const e = res.explanation || {};
  return {
    sentence: d.sentence,
    concepts: (d.overall || []).map((r) => ({ term: r.term, category: r.category, score: r.score })),
    features: (e.atoms || []).map((a) => ({ atom: a.atom, act: a.activation, means: (a.means || []).map((m) => m.term) })),
    attribution: (e.attribution || []).map((at) => ({
      term: at.term,
      supporting: (at.supporting_atoms || []).map((s) => ({
        term: s.means && s.means[0] ? s.means[0].term : '',
        contribution: s.contribution,
      })),
    })),
  };
}

export default function Describe() {
  const [token, setToken] = useState(getToken);
  const [phase, setPhase] = useState(() => (getToken() ? 'unlocked' : 'locked'));
  const [passInput, setPassInput] = useState('');
  const [lockError, setLockError] = useState('');

  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const checkHealth = useCallback(async (tok) => {
    try {
      const res = await fetch(`${DESCRIBE_BASE}/health`, { headers: { Authorization: `Bearer ${tok}` } });
      if (res.status === 401) return 'unauthorized';
      if (!res.ok) return 'offline';
      return 'online';
    } catch (e) { return 'offline'; }
  }, []);

  async function unlock(e) {
    e.preventDefault();
    const tok = passInput.trim();
    if (!tok) return;
    setLockError('');
    const r = await checkHealth(tok);
    if (r === 'unauthorized') { setLockError('Wrong password.'); return; }
    if (r === 'offline') { setLockError("Can't reach your Mac — is it on with the describe service + tunnel running?"); return; }
    saveToken(tok);
    setToken(tok);
    setPassInput('');
    setPhase('unlocked');
  }

  function lock() {
    clearToken();
    setToken('');
    setResult(null);
    setPreview(null);
    setPhase('locked');
  }

  const analyze = useCallback(async (file) => {
    if (!file) return;
    setBusy(true); setError(''); setResult(null);
    setPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${DESCRIBE_BASE}/describe`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (res.status === 401) { lock(); return; }
      if (!res.ok) { setError('Analysis failed — try another image.'); return; }
      setResult(toPanel(await res.json()));
    } catch (e) {
      setError("Couldn't reach the Mac. Is it on with the describe service running?");
    } finally {
      setBusy(false);
    }
  }, [token]);

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) analyze(f);
  }

  if (phase === 'locked') {
    return (
      <div className="drop-lock">
        <form className="drop-lock-card" onSubmit={unlock}>
          <h1>Describe</h1>
          <p className="drop-sub">Enter the password to analyze a photo with CLIP + the SAE.</p>
          <input type="password" value={passInput} onChange={(e) => setPassInput(e.target.value)} placeholder="Password" autoFocus />
          <button type="submit">Unlock</button>
          {lockError && <p className="drop-error">{lockError}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="describe-page">
      <div className="drop-bar">
        <span className="drop-title">Describe a photo</span>
        <button className="drop-lockbtn" style={{ marginLeft: 'auto' }} onClick={lock}>Lock</button>
      </div>

      <div
        className={`drop-zone${dragging ? ' over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        style={{ minHeight: 140, marginTop: 14 }}
      >
        <p className="drop-muted">
          Drag a photo here or{' '}
          <button className="desc-link" onClick={() => fileRef.current?.click()}>choose a file</button>
          {' '}— CLIP describes it, the SAE explains why.
        </p>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => { if (e.target.files[0]) analyze(e.target.files[0]); e.target.value = ''; }} />
        {dragging && <div className="drop-overlay">Drop to analyze</div>}
      </div>

      {error && <div className="drop-banner" style={{ marginTop: 12 }}>{error}</div>}

      {(preview || busy) && (
        <div className="describe-result">
          {preview && <img className="describe-preview" src={preview} alt="to analyze" />}
          <div className="describe-out">
            {busy && <p className="drop-muted">Analyzing on the Mac…</p>}
            {result && <DescribePanel data={result} defaultOpen />}
          </div>
        </div>
      )}
    </div>
  );
}
